import type { RealtimeEvent } from "./types";

/**
 * Incremental text/event-stream parser (the subset of the SSE spec we rely on): `event:` /
 * `data:` / `id:` fields, `:` comment lines (heartbeats) ignored, blank line dispatches.
 * Feed it decoded chunks as they arrive — it buffers partial lines across chunks.
 */
export class SseParser {
  private buffer = "";
  private eventType = "message";
  private dataLines: string[] = [];
  private lastEventId: string | undefined;

  constructor(private readonly onEvent: (event: RealtimeEvent) => void) {}

  feed(chunk: string): void {
    this.buffer += chunk;
    let newline: number;
    while ((newline = this.buffer.indexOf("\n")) >= 0) {
      const line = this.buffer.slice(0, newline).replace(/\r$/, "");
      this.buffer = this.buffer.slice(newline + 1);
      this.processLine(line);
    }
  }

  private processLine(line: string): void {
    if (line === "") {
      this.dispatch();
      return;
    }
    if (line.startsWith(":")) return; // comment (heartbeat ping)
    const colon = line.indexOf(":");
    const field = colon === -1 ? line : line.slice(0, colon);
    let value = colon === -1 ? "" : line.slice(colon + 1);
    if (value.startsWith(" ")) value = value.slice(1);
    switch (field) {
      case "event":
        this.eventType = value;
        break;
      case "data":
        this.dataLines.push(value);
        break;
      case "id":
        if (!value.includes("\0")) this.lastEventId = value;
        break;
      // "retry" and unknown fields: ignored (the client owns its backoff policy).
    }
  }

  private dispatch(): void {
    if (this.dataLines.length === 0) {
      this.eventType = "message";
      return;
    }
    this.onEvent({
      type: this.eventType,
      data: this.dataLines.join("\n"),
      lastEventId: this.lastEventId,
    });
    this.eventType = "message";
    this.dataLines = [];
  }
}
